"use client";

import {
  Stack,
  Box,
  Text,
  Flex,
  Input,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Image,
  Button,
  Badge,
  IconButton,
  Tooltip,
  VStack,
  HStack,
  Divider,
  InputGroup,
  InputLeftElement,
  Skeleton,
  SkeletonText,
  Icon,
  SimpleGrid,
  Center,
} from "@chakra-ui/react";
import { AddIcon, SearchIcon, ChevronRightIcon, ViewIcon, EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { FiFileText, FiEye, FiClock } from "react-icons/fi";
import deleteIcon from "common/assets/img/deleteIcon.png";
import editIcon from "common/assets/img/editIcon.png";
import { globalStyles } from "common/theme/styles";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import ActionNavbar from "common/components/actionBar/ActionNavbar";
import { useDispatch, useSelector } from "react-redux";
import { recruitConstant } from "services/Recruit/constant";
import { toast } from "react-toastify";
import { HasAccess } from "common/utils/accessUtils";
import { accessModuleTitles } from "common/constant";
import { getPolicyFormattedDate } from "services/Crm/utils/calcUtils";
import CompanyPolicyModal from "common/components/companyPolicyModel";
import CompanySubPolicyModel from "common/components/companySubPolicyModel";
import { addCompanyPolicy } from "common/redux/reducers/companyPolicySlice";
import { deleteCompanyPolicy } from "common/redux/reducers/companyPolicySlice";
import { editCompanyPolicy } from "common/redux/reducers/companyPolicySlice";
import CommonDeleteModel from "common/components/commonDeleteModel";
import { deleteCompanySubPolicy } from "common/redux/reducers/companyPolicySlice";
import { addCompanySubPolicy } from "common/redux/reducers/companyPolicySlice";
import { editCompanySubPolicy } from "common/redux/reducers/companyPolicySlice";
import PreviewRichText from "services/Recruit/components/PreviewRichText";
import { getCompanyPolicy } from "common/redux/reducers/companyPolicySlice";
import { getLoggedInUser, isEmployee as checkIsEmployee } from "common/utils/roleUtils";

const CompanyPolicy = () => {
  const colors = globalStyles.colors;
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const [isCreateCompanyPolicyModelOpen, setIsCreateCompanyPolicyModelOpen] = useState(false);
  const [isEditCompanyPolicyModalOpen, setIsEditCompanyPolicyModalOpen] = useState(false);
  const [selectedPolicyForEdit, setSelectedPolicyForEdit] = useState(null);
  const [isCreateCompanySubPolicyModelInfo, setIsCreateCompanySubPolicyModelInfo] = useState({
    info: -1,
    isOpen: false,
    type: 'create',
    contentType: 'editor'
  });
  const [previewInfo, setPreviewInfo] = useState({
    isOpen: false,
    title: "",
    content: "",
    pdfUrl: ""
  });

  const { data, getLoading } = useSelector((state) => state.companyPolicy);
  const [isDeleteModelInfo, setIsDeleteModelInfo] = useState({
    isOpen: false,
    info: -1,
    type: 'policy'
  });
  const [permission] = HasAccess([accessModuleTitles.CompanyPolicies]);
  const location = useLocation();
  const { mode } = location.state || {};
  const inputRef = useRef(null);
  const loggedInUser = getLoggedInUser();
  const isEmployeeActiveRole = checkIsEmployee(loggedInUser);

  const visiblePolicies = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!isEmployeeActiveRole) return data;

    return data.filter((policy) => Array.isArray(policy?.subPolicyList) && policy.subPolicyList.length > 0);
  }, [data, isEmployeeActiveRole]);

  // Get selected policy
  const selectedPolicy = visiblePolicies.find((policy) => policy._id === selectedPolicyId);

  // Filter policies based on search
  const filteredPolicies = visiblePolicies.filter((policy) =>
    policy.policyName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeletePolicy = (policyId) => {
    if (policyId) {
      setIsDeleteModelInfo({ isOpen: true, info: policyId, type: 'policy' });
    }
  };

  async function onPolicyDeleteHandler() {
    try {
      if (isDeleteModelInfo.type === 'policy') {
        await dispatch(deleteCompanyPolicy(isDeleteModelInfo.info)).unwrap();
        if (selectedPolicyId === isDeleteModelInfo.info) {
          setSelectedPolicyId(data?.[0]?._id !== isDeleteModelInfo.info ? data?.[0]?._id : data?.[1]?._id || null);
        }
      } else {
        await dispatch(deleteCompanySubPolicy(isDeleteModelInfo.info)).unwrap();
      }
    } catch (err) {
      toast.error(isDeleteModelInfo.type === 'policy' ? "Failed to delete policy" : "Failed to delete sub policy");
    } finally {
      setIsDeleteModelInfo({ isOpen: false, info: -1, type: 'policy' });
    }
  }

  async function getCompanyData() {
    await dispatch(getCompanyPolicy());
  }

  useEffect(() => {
    getCompanyData();
  }, []);

  // Auto-select first visible policy when data loads or selection becomes hidden
  useEffect(() => {
    if (visiblePolicies.length === 0) {
      if (selectedPolicyId) {
        setSelectedPolicyId(null);
      }
      return;
    }

    const hasVisibleSelection = visiblePolicies.some((policy) => policy._id === selectedPolicyId);
    if (!hasVisibleSelection) {
      setSelectedPolicyId(visiblePolicies[0]._id);
    }
  }, [visiblePolicies, selectedPolicyId]);

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      if (isCreateCompanyPolicyModelOpen) {
        const res = await dispatch(addCompanyPolicy({ values, resetForm })).unwrap();
        if (Object.keys(res).length > 0) {
          setIsCreateCompanyPolicyModelOpen(false);
          resetForm();
          // Select newly created policy
          if (res._id) {
            setSelectedPolicyId(res._id);
          }
        }
      } else if (isEditCompanyPolicyModalOpen) {
        const res = await dispatch(editCompanyPolicy({
          policyId: selectedPolicyForEdit._id,
          values
        })).unwrap();
        if (Object.keys(res).length > 0) {
          setIsEditCompanyPolicyModalOpen(false);
          setSelectedPolicyForEdit(null);
          resetForm();
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(isCreateCompanyPolicyModelOpen ? "Failed to create company policy" : "Failed to update company policy");
    }
  };

  const handleSubPolicyFormSubmit = async (values, { resetForm }) => {
    try {
      if (isCreateCompanySubPolicyModelInfo.type === 'create') {
        const updatedValue = { ...values, policyId: isCreateCompanySubPolicyModelInfo.info };
        await dispatch(addCompanySubPolicy({ values: updatedValue, resetForm }));
      } else {
        const updatedEditValue = { ...values, info: isCreateCompanySubPolicyModelInfo.info };
        await dispatch(editCompanySubPolicy({ values: updatedEditValue, resetForm }));
      }
      setIsCreateCompanySubPolicyModelInfo({
        info: -1,
        isOpen: false,
        type: 'create',
        contentType: 'editor'
      });
      resetForm();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <Flex h="calc(100vh - 180px)" gap={0}>
      <Box w="320px" borderRight="1px solid" borderColor="gray.200" p={4}>
        <Skeleton height="40px" mb={4} />
        <VStack spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} height="60px" w="100%" borderRadius="md" />
          ))}
        </VStack>
      </Box>
      <Box flex={1} p={6}>
        <Skeleton height="32px" w="200px" mb={4} />
        <SkeletonText noOfLines={4} spacing={4} />
      </Box>
    </Flex>
  );

  // Empty state component
  const EmptyState = ({ title, description, action, actionLabel, isDark = false }) => (
    <VStack
      spacing={4}
      py={16}
      px={8}
      textAlign="center"
      color={isDark ? "whiteAlpha.900" : "gray.500"}
    >
      <Box
        w="80px"
        h="80px"
        borderRadius="full"
        bg={isDark ? "whiteAlpha.200" : "gray.100"}
        display="flex"
        alignItems="center"
        justifyContent="center"
        color={isDark ? "white" : "gray.400"}
      >
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </Box>
      <Text fontWeight="600" fontSize="lg" color={isDark ? "white" : "gray.700"}>{title}</Text>
      <Text fontSize="sm" color={isDark ? "whiteAlpha.800" : "gray.500"} maxW="300px">{description}</Text>
      {action && (
        <Button
          leftIcon={<AddIcon />}
          bg={isDark ? "white" : "#3C2FC0"}
          color={isDark ? "#3C2FC0" : "white"}
          _hover={isDark ? {
            bg: "whiteAlpha.900",
            transform: "translateY(-1px)",
            shadow: "md"
          } : {
            bg: "#2A2098",
            transform: "translateY(-1px)",
            shadow: "md"
          }}
          size="sm"
          onClick={action}
          transition="all 0.2s"
          px={6}
          borderRadius="lg"
          fontWeight="700"
        >
          {actionLabel}
        </Button>
      )}
    </VStack>
  );

  return (
    <>
      <ActionNavbar isCreatePage={true} />
      <Box bg="gray.50" minH="calc(100vh - 60px)">
        {/* Header */}
        <Box
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
          px={isEmployeeActiveRole ? 4 : 6}
          py={4}
        >
          <Flex justify="space-between" align="center">
            <Box>
              <Text
                fontSize={isEmployeeActiveRole ? "24px" : "xl"}
                fontWeight={isEmployeeActiveRole ? "700" : "600"}
                lineHeight="1.2"
                color={isEmployeeActiveRole ? colors.pageTitleColor : "gray.800"}
              >
                {recruitConstant.CompanyPolicies}
              </Text>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Manage your organization's policies and guidelines
              </Text>
            </Box>
            {permission?.create && (
              <Button
                leftIcon={<AddIcon />}
                bgColor="#3C2FC0"
                color="white"
                size="sm"
                onClick={() => setIsCreateCompanyPolicyModelOpen(true)}
                _hover={{ transform: "translateY(-1px)", shadow: "md" }}
                transition="all 0.2s"
              >
                Create Policy
              </Button>
            )}
          </Flex>
        </Box>

        {/* Main Content */}
        {getLoading ? (
          <LoadingSkeleton />
        ) : (
          <Flex h="calc(100vh - 180px)">
            {/* Left Sidebar - Policy List */}
            <Box
              w="320px"
              bg="white"
              borderRight="1px solid"
              borderColor="gray.200"
              display="flex"
              flexDirection="column"
            >
              {/* Search */}
              <Box p={5} borderBottom="1px solid" borderColor="gray.100">
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" mt="-1px" />
                  </InputLeftElement>
                  <Input
                    placeholder="Quick search policies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    borderRadius="lg"
                    bg="gray.50"
                    border="none"
                    _placeholder={{ color: "gray.400" }}
                    _focus={{ bg: "white", shadow: "sm", ring: "1px", ringColor: "blue.400" }}
                    transition="all 0.2s"
                  />
                </InputGroup>
              </Box>

              {/* Policy List */}
              <Box flex={1} overflowY="auto" p={2}>
                {filteredPolicies.length === 0 ? (
                  <EmptyState
                    title="No policies found"
                    description={searchTerm ? "Try a different search term" : "Create your first company policy to get started"}
                    action={!searchTerm && permission?.create ? () => setIsCreateCompanyPolicyModelOpen(true) : null}
                    actionLabel="Create Policy"
                  />
                ) : (
                  <VStack spacing={1} align="stretch">
                    {filteredPolicies.map((policy) => (
                      <Box
                        key={policy._id}
                        p={4}
                        borderRadius="xl"
                        cursor="pointer"
                        bg={selectedPolicyId === policy._id ? "blue.50" : "transparent"}
                        onClick={() => setSelectedPolicyId(policy._id)}
                        _hover={{
                          bg: selectedPolicyId === policy._id ? "blue.50" : "gray.50"
                        }}
                        transition="all 0.2s"
                        role="group"
                        position="relative"
                      >
                        {selectedPolicyId === policy._id && (
                          <Box
                            position="absolute"
                            left="0"
                            top="25%"
                            bottom="25%"
                            w="4px"
                            bg="#3C2FC0"
                            borderRadius="full"
                          />
                        )}
                        <Flex justify="space-between" align="center">
                          <HStack spacing={3} flex={1}>
                            <Icon
                              as={FiFileText}
                              boxSize={4}
                              color={selectedPolicyId === policy._id ? "#3C2FC0" : "gray.400"}
                            />
                            <HStack spacing={2} flex={1}>
                              <Text
                                fontWeight={selectedPolicyId === policy._id ? "700" : "600"}
                                fontSize="sm"
                                color={selectedPolicyId === policy._id ? "blue.700" : "gray.700"}
                                noOfLines={1}
                              >
                                {policy.policyName}
                              </Text>
                              <Box
                                minW="20px"
                                h="20px"
                                bg={selectedPolicyId === policy._id ? "#3C2FC0" : "#F0EFFF"}
                                color={selectedPolicyId === policy._id ? "white" : "gray.600"}
                                borderRadius="4px" // Square with slightly rounded corners
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontSize="10px"
                                fontWeight="800"
                                px={1}
                              >
                                {policy.subPolicyList?.length || 0}
                              </Box>
                            </HStack>
                          </HStack>

                          <HStack
                            spacing={0}
                            transition="opacity 0.2s"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {permission?.update && (
                              <Tooltip label="Rename" fontSize="xs">
                                <IconButton
                                  icon={<EditIcon fontSize="xs" title="Edit" />}
                                  size="xs"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPolicyForEdit(policy);
                                    setIsEditCompanyPolicyModalOpen(true);
                                  }}
                                  _hover={{ bg: "blue.100", color: "blue.600" }}
                                />
                              </Tooltip>
                            )}
                            {permission?.delete && (
                              <Tooltip label="Delete" fontSize="xs">
                                <IconButton
                                  icon={<DeleteIcon fontSize="xs" title="Delete" />}
                                  size="xs"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePolicy(policy._id);
                                  }}
                                  _hover={{ bg: "red.100", color: "red.600" }}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </Box>

            {/* Right Content - Sub-policies */}
            <Box flex={1} bg="gray.50" overflowY="auto">
              {selectedPolicy ? (
                <Box p={6}>
                  {/* Policy Header */}
                  <Flex justify="space-between" align="stretch" mb={6}>
                    <Box flex={1}>
                      <Text fontSize="xl" fontWeight="600" color="gray.800">
                        {selectedPolicy.policyName}
                      </Text>
                      <Text fontSize="sm" color="gray.500" fontWeight="500">
                        {selectedPolicy.subPolicyList?.length || 0} {selectedPolicy.subPolicyList?.length > 1 ? 'Sub-Policies' : 'Sub-Policy'}
                      </Text>
                      {selectedPolicy.description && (
                        <Text fontSize="sm" width="100%" color="gray.600" mt={3} p={3} borderRadius="md">
                          {selectedPolicy.description}
                        </Text>
                      )}
                    </Box>
                    {permission?.create && (
                      <Button
                        leftIcon={<AddIcon />}
                        colorScheme="#3C2FC0"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsCreateCompanySubPolicyModelInfo({
                            info: selectedPolicy._id,
                            isOpen: true,
                            type: 'create',
                            contentType: 'editor'
                          });
                        }}
                        _hover={{ bg: "#3C2FC0",color:"white" }}
                        ml={4}
                      >
                        Add Sub-Policy
                      </Button>
                    )}
                  </Flex>

                  {/* Sub-policies Card List */}
                  {selectedPolicy.subPolicyList?.length > 0 ? (
                    <SimpleGrid columns={1} spacing={4}>
                      {selectedPolicy.subPolicyList.map((item) => (
                        <Box
                          key={item._id}
                          p={5}
                          bg="white"
                          borderRadius="xl"
                          border="1px solid"
                          borderColor="gray.100"
                          shadow="sm"
                          transition="all 0.2s"
                          _hover={{ shadow: "md", borderColor: "blue.100", transform: "translateY(-1px)" }}
                        >
                          <Flex justify="space-between" align="center">
                            <HStack spacing={4} flex={1}>
                              <Center w="40px" h="40px" bg="blue.50" borderRadius="lg" color="#3C2FC0">
                                <Icon as={FiFileText} boxSize={5} />
                              </Center>
                              <Box flex={1}>
                                <Text fontWeight="600" color="gray.800" fontSize="md">
                                  {item.title}
                                </Text>
                                <HStack spacing={3} mt={1}>
                                  <HStack spacing={1} color="gray.400">
                                    <Icon as={FiClock} boxSize={3} />
                                    <Text fontSize="xs">
                                      {getPolicyFormattedDate(item.updatedAt || item.createdAt)}
                                    </Text>
                                  </HStack>
                                  {item.description && (
                                    <Text fontSize="xs" color="gray.400" noOfLines={1} borderLeft="1px solid" borderColor="gray.200" pl={3}>
                                      {item.description}
                                    </Text>
                                  )}
                                </HStack>
                              </Box>
                            </HStack>

                            <HStack spacing={2}>
                              <Tooltip label="View Content" fontSize="xs">
                                <IconButton
                                  icon={<Icon as={FiEye} />}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={() => setPreviewInfo({
                                    isOpen: true,
                                    title: item.title,
                                    content: item.content,
                                    pdfUrl: item.pdfUrl
                                  })}
                                  _hover={{ bg: "blue.50" }}
                                />
                              </Tooltip>

                              {permission?.update && (
                                <Tooltip label="Edit" fontSize="xs">
                                  <IconButton
                                    icon={<EditIcon />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      const { _id, ...rest } = item;
                                      setIsCreateCompanySubPolicyModelInfo({
                                        info: {
                                          policyId: selectedPolicy._id,
                                          subPolicyInfo: {
                                            id: item._id,
                                            info: {
                                              ...rest,
                                              contentType: item?.content ? 'editor' : item?.pdfUrl ? 'pdf' : ''
                                            }
                                          }
                                        },
                                        isOpen: true,
                                        type: 'edit'
                                      });
                                    }}
                                    _hover={{ bg: "blue.50" }}
                                  />
                                </Tooltip>
                              )}

                              {permission?.delete && (
                                <Tooltip label="Delete" fontSize="xs">
                                  <IconButton
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => setIsDeleteModelInfo({
                                      isOpen: true,
                                      info: {
                                        policyId: selectedPolicy._id,
                                        subPolicyId: item._id,
                                      },
                                      type: 'subPolicy'
                                    })}
                                    _hover={{ bg: "red.50" }}
                                  />
                                </Tooltip>
                              )}
                            </HStack>
                          </Flex>
                        </Box>
                      ))}
                    </SimpleGrid>
                  ) : (
                    <Box borderRadius="xl" shadow="lg" bg="white" >
                      <EmptyState
                        title="No Sub-Policies yet"
                        description="Add Sub-Policies to organize detailed guidelines under this policy"
                        action={permission?.create ? () => {
                          setIsCreateCompanySubPolicyModelInfo({
                            info: selectedPolicy._id,
                            isOpen: true,
                            type: 'create',
                            contentType: 'editor'
                          });
                        } : null}
                        actionLabel="Add Sub-Policy"
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <EmptyState
                  title="Select a policy"
                  description="Choose a policy from the left sidebar to view its details"
                />
              )}
            </Box>
          </Flex>
        )}

        {/* Modals */}
        <CommonDeleteModel
          isOpen={isDeleteModelInfo.isOpen}
          onClose={() => setIsDeleteModelInfo({ isOpen: false, info: -1, type: 'policy' })}
          type={isDeleteModelInfo.type === 'policy' ? 'Company Policy' : 'Company Sub-Policy'}
          handleDeleteData={() => onPolicyDeleteHandler()}
          ids={isDeleteModelInfo.type === 'policy' ? isDeleteModelInfo.info : isDeleteModelInfo.info.subPolicyId}
          notCheckbox={true}
        />
        <CompanyPolicyModal
          handleFormSubmit={handleFormSubmit}
          onClose={() => setIsCreateCompanyPolicyModelOpen(false)}
          isOpen={isCreateCompanyPolicyModelOpen}
        />
        <CompanyPolicyModal
          handleFormSubmit={handleFormSubmit}
          onClose={() => {
            setIsEditCompanyPolicyModalOpen(false);
            setSelectedPolicyForEdit(null);
          }}
          isOpen={isEditCompanyPolicyModalOpen}
          type="edit"
          policyData={selectedPolicyForEdit}
        />
        <CompanySubPolicyModel
          handleFormSubmit={handleSubPolicyFormSubmit}
          onClose={() => setIsCreateCompanySubPolicyModelInfo({
            info: -1,
            isOpen: false,
            type: 'create',
            contentType: 'editor'
          })}
          info={isCreateCompanySubPolicyModelInfo}
          isOpen={isCreateCompanySubPolicyModelInfo.isOpen}
        />
        <PreviewRichText
          isOpen={previewInfo.isOpen}
          onClose={() => setPreviewInfo({ ...previewInfo, isOpen: false })}
          title={previewInfo.title}
          html={previewInfo.content}
          pdfUrl={previewInfo.pdfUrl}
          previewFormat="html"
        />
      </Box>
    </>
  );
};

export default CompanyPolicy;
